import { MediaKind, MediaLimits } from './media-api';
import { Sniffed } from './media-sniff';

/** What the browser could learn about a chosen file before anything is sent. */
export interface FileFacts {
  name: string;
  size: number;
  /** `null` = the first bytes are none of the allowed formats. */
  sniffed: Sniffed | null;
  /** The sides of a picture or of a video (`null` = the browser cannot read this format, e.g. HEIC: the job checks it later). */
  dims?: { width: number; height: number } | null;
  /** The length of a video (`null` = unknown). */
  durationSec?: number | null;
}

/** Why a file is not accepted; the page tells it to the editor in words (`checks.*` in the texts). */
export type RejectReason = 'empty' | 'type' | 'tooLarge' | 'replaceType' | 'photosLimit' | 'videoLimit' | 'tooSmall' | 'tooLong' | 'videoTooSmall';

export interface Verdict {
  ok: boolean;
  reason?: RejectReason;
  /** The numbers the text of the reason needs. */
  data: Record<string, number>;
  /** Accepted, but a photo whose short side is below `photoGoodSide`: it will be soft on a big screen. */
  lowRes: boolean;
}

export interface Counts {
  photos: number;
  videos: number;
}

export interface CheckOptions {
  limits: MediaLimits;
  /** The live items of the size that count against the limits. */
  counts: Counts;
  /** A file that replaces an item: it must be of the kind of that item and it does not count against the limits. */
  replaceKind?: MediaKind;
}

/** The job accepts a video up to this much over the limit (it measures the length of the container, which is a little over the stream). */
const DURATION_SLACK_SEC = 0.5;

function bad(reason: RejectReason, data: Record<string, number> = {}): Verdict {
  return { ok: false, reason, data, lowRes: false };
}

/** The pre-check of one file (§5 of the brief: type, size, limits, and the sides / the length where the browser can read them). The server and the job check again. */
export function checkFile(file: FileFacts, options: CheckOptions): Verdict {
  const { limits, counts, replaceKind } = options;
  if (file.size <= 0) {
    return bad('empty');
  }
  const type = file.sniffed?.type;
  if (!type) {
    return bad('type');
  }
  const max = type === 'VIDEO' ? limits.videoBytes : limits.photoBytes;
  if (file.size > max) {
    return bad('tooLarge', { size: file.size, max });
  }
  if (replaceKind) {
    if (type !== replaceKind) {
      return bad('replaceType');
    }
  } else if (type === 'PHOTO' && counts.photos >= limits.photos) {
    return bad('photosLimit', { max: limits.photos });
  } else if (type === 'VIDEO' && counts.videos >= limits.videos) {
    return bad('videoLimit', { max: limits.videos });
  }
  const dims = file.dims;
  if (type === 'PHOTO') {
    if (dims) {
      const side = Math.min(dims.width, dims.height);
      if (side < limits.photoMinSide) {
        return bad('tooSmall', { width: dims.width, height: dims.height, min: limits.photoMinSide, good: limits.photoGoodSide });
      }
      return { ok: true, data: { width: dims.width, height: dims.height, good: limits.photoGoodSide }, lowRes: side < limits.photoGoodSide };
    }
    return { ok: true, data: {}, lowRes: false };
  }
  if (file.durationSec != null && file.durationSec > limits.videoSeconds + DURATION_SLACK_SEC) {
    return bad('tooLong', { seconds: Math.round(file.durationSec), max: limits.videoSeconds });
  }
  if (dims && Math.min(dims.width, dims.height) < limits.videoMinSide) {
    return bad('videoTooSmall', { width: dims.width, height: dims.height, min: limits.videoMinSide });
  }
  return { ok: true, data: {}, lowRes: false };
}

export interface PlannedFile {
  file: FileFacts;
  verdict: Verdict;
}

export interface BatchPlan {
  accepted: PlannedFile[];
  rejected: PlannedFile[];
}

/**
 * The files chosen at once, in their order: each one is checked against what the size has plus what the files before it will take
 * (the sixth photo of a batch is refused, the first five go). A replacement does not take a place.
 */
export function planBatch(files: readonly FileFacts[], options: CheckOptions): BatchPlan {
  const counts: Counts = { ...options.counts };
  const plan: BatchPlan = { accepted: [], rejected: [] };
  for (const file of files) {
    const verdict = checkFile(file, { ...options, counts });
    if (!verdict.ok) {
      plan.rejected.push({ file, verdict });
      continue;
    }
    plan.accepted.push({ file, verdict });
    if (!options.replaceKind) {
      if (file.sniffed?.type === 'VIDEO') {
        counts.videos++;
      } else {
        counts.photos++;
      }
    }
  }
  return plan;
}
